import { useMemo } from 'react';

import { WidgetViewMode } from 'utilitys/reporting/widgetViewModeUtils';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import {
    arrayBufferToString,
    generateTabs,
} from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { useUpdateIfReady } from 'components/Dashboards/Reporting/widgetInReport/hooks/RefreshWidgetPayload/useUpdateIfReady';
import { UseLargestDeletedActivitiesProvider } from 'components/widgets/LargestDeletedActivities/LargestDeletedActivities.provider';

interface Props {
    ui: UseLargestDeletedActivitiesProvider['ui'];
    api: UseLargestDeletedActivitiesProvider['api'];
    title: string;
    subTitle: string;
    setIsPayloadReady?: (newValue: boolean) => void;
    widgetViewMode?: WidgetViewMode;
}

export const useLargestDeletedActivitiesLogic = ({
    ui,
    api,
    title,
    subTitle,
    setIsPayloadReady,
    widgetViewMode,
}: Props) => {
    const isPayloadReady = () => {
        const currentPayload = api.getLargestDeletedActivitiesTableData.payload;
        if (!currentPayload) return;
        if (
            currentPayload.compareVersionId === undefined ||
            currentPayload.latestVersionId === undefined ||
            currentPayload.contractId === undefined ||
            currentPayload.contractId === null ||
            currentPayload.projectId === undefined ||
            currentPayload.projectId === null
        ) {
            return;
        }
        setIsPayloadReady && setIsPayloadReady(true);
    };

    useUpdateIfReady({ isPayloadReadyCallback: isPayloadReady, widgetViewMode: widgetViewMode });

    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();

    const tableData = useMemo(() => {
        if (api.getLargestDeletedActivitiesTableData?.instance.data?.periodItemsList) {
            const periodItemsList = api.getLargestDeletedActivitiesTableData.instance.data.periodItemsList;
            return (
                periodItemsList.find((item) => item.id === ui.state.activeTab)?.itemsList ||
                periodItemsList[0].itemsList
            );
        }
        return [];
    }, [api.getLargestDeletedActivitiesTableData.instance.data, ui.state.activeTab]);

    const handleAPIExcelExport = () => {
        api.exportCsvReport.instance.mutate(api.exportCsvReport.payload, {
            onSuccess: () => {
                handleSnackBar('File Successfully Downloaded', 'success');

                sendEventWithDimensions({
                    category: 'Export',
                    action: 'Excel',
                    label: `${title} ${subTitle}`,
                });
            },
            onError: (error) => {
                const message = arrayBufferToString(error?.response?.data);
                DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
            },
        });
    };

    const tabsData = generateTabs({ data: api.getLargestDeletedActivitiesTableData.instance.data });
    return {
        logic: {
            handleAPIExcelExport,
            tableData,
            tabsData,
        },
    };
};
